import { cva } from 'class-variance-authority'
import { ChangeEvent, TextareaHTMLAttributes, useCallback, useEffect, useState } from 'react'

import { cn } from '@/utils/style'

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  value?: string
  disabled?: boolean
  allowClear?: boolean
  onChange?: (value: ChangeEvent<HTMLTextAreaElement>) => void
  onClear?: () => void
  validator?: (value: string) => boolean
  className?: string
}

const textareaVariants = cva(
  'w-full min-h-[120px] p-4 rounded-lg border-[2px] t-sb border-gray100 bg-panel-white text-primary placeholder:text-tertiary outline-none focus:border-secondary resize-none',
  {
    variants: {
      disabled: {
        true: 'cursor-not-allowed bg-panel-gray-100 text-tertiary',
      },
      error: {
        true: 'border-red-500 focus:border-red-500',
      },
    },
  },
)

export function Textarea({
  value,
  onChange,
  onClear,
  disabled = false,
  allowClear = false,
  validator,
  className,
  ...rest
}: TextareaProps) {
  const [v, setV] = useState(value)
  const [error, setError] = useState(false)

  useEffect(() => {
    setV(value)
  }, [value])

  const onVChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value
      setV(newValue)
      setError(validator ? !validator(newValue) : false)

      if (onChange) {
        onChange(e)
      }
    },
    [onChange, validator],
  )

  const handleClear = useCallback(() => {
    setV('')
    setError(false)
    if (onClear) {
      onClear()
    }
  }, [onClear])

  return (
    <div className="relative">
      <textarea
        value={v}
        onChange={onVChange}
        className={cn(textareaVariants({ disabled, error }), className)}
        disabled={disabled}
        {...rest}
      />
      {allowClear && !disabled && v && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-3 text-tertiary hover:text-secondary"
        >
          ✕
        </button>
      )}
    </div>
  )
}
